<template>
  <div
    v-if="event.scheduleVisible"
    class="muted--text"
    :class="`text-${alignText}`"
    style="font-size: 16px"
  >
    {{ $t("events:EventCard.registrationDeadline.text") }}
    <template
      v-if="
        !event.registrationEndDatesPerCompetition || deadlineDates.length == 1
      "
    >
      {{ deadline }}
    </template>
    <template
      v-else-if="
        event.registrationEndDatesPerCompetition && showApplicableCheckSchedule
      "
    >
      {{
        $t(
          "events:EventCard.registrationDeadline.checkSchedule.text",
          "check schedule"
        )
      }}
    </template>
    <template v-else>
      <template v-if="$vuetify.breakpoint.mdAndUp">
        {{ groupedDeadlines.join("; ") }}
      </template>
      <template v-else>
        <div
          v-for="deadlines in groupedDeadlines"
          :key="deadlines"
          class="my-1"
        >
          {{ deadlines }}
        </div>
      </template>
    </template>
  </div>
</template>
<script>
import { defineComponent, toRefs, computed } from "vue";
import gql from "fraql";
import { groupBy, clone } from "lodash-es";
import dayjs from "@/plugins/dayjs";
import { useCompetitionHelpers } from "@composables/competition_helpers";
import { useTranslation } from "@/plugins/i18next/i18next";

export default defineComponent({
  name: "AppShowRegistrationDeadline",
  props: {
    event: {
      type: Object,
      required: true
    },
    showApplicableCheckSchedule: {
      type: Boolean,
      default: false
    },
    alignText: {
      type: String,
      default: "left"
    }
  },
  setup(props) {
    const { t } = useTranslation();
    const { event } = toRefs(props);
    const { competitionOrderFn } = useCompetitionHelpers();

    function groupedByRegistrationEndDate() {
      return groupBy(
        event.value.competitions.reduce((comps, competition) => {
          if (competition.forResultsOnly) return comps;

          competition = clone(competition);
          if (!competition.registrationEndDate) {
            // Default registrationEndDate will be event's
            competition.registrationEndDate = event.value.registrationEndDate;
          }
          comps.push(competition);
          return comps;
        }, []),
        "registrationEndDate"
      );
    }

    const deadlineDates = computed(() => {
      return Object.keys(groupedByRegistrationEndDate());
    });

    const groupedDeadlines = computed(() => {
      const sortFn = competitionOrderFn.value;
      const dates = Object.entries(groupedByRegistrationEndDate()).sort(
        (a, b) => new Date(a[0]) - new Date(b[0])
      );

      return dates.map(([date, comps], index) => {
        if (dates.length > 1 && dates.length - 1 == index && comps.length > 3) {
          return (
            t("events:EventCard.openForEntriesDeadline.others.text", "others") +
            " - " +
            dayjs.date(date)
          );
        }
        const codes = comps
          .map(x => x.code)
          .sort((a, b) => sortFn(a, b))
          .join(", ");
        return codes + " - " + dayjs.date(date);
      });
    });

    const deadline = computed(() => {
      if (event.value.registrationEndDatesPerCompetition) {
        return dayjs.date(deadlineDates.value[0]);
      }
      return dayjs.date(event.value.registrationEndDate);
    });

    return { deadline, groupedDeadlines, deadlineDates };
  },
  fragments: {
    eventFields: gql`
      fragment _ on Event {
        id
        registrationEndDate
        registrationEndDatesPerCompetition
        scheduleVisible
      }
    `,
    competitionFields: gql`
      fragment _ on Competition {
        id
        code
        registrationEndDate
        forResultsOnly
      }
    `
  }
});
</script>
